const CITY_WORD_BOUNDARY = /([ \-'])/;
const CITY_MINOR_WORDS = new Set(["of", "the", "and", "on", "in", "at", "by"]);
const SCOTS_PREFIXED_WORD = /^mc([a-z]{2,})$/;
const CITY_CASING_EXCEPTIONS = new Map<string, string>(
  [
    ["FL", "LaBelle"],
    ["GA", "LaGrange"],
    ["NE", "LaVista"],
    ["TX", "LaPorte"],
  ].map(([state, name]) => [`${state}:${name.toLowerCase()}`, name]),
);

function capitaliseCityWord(word: string): string {
  const scotsPrefixed = SCOTS_PREFIXED_WORD.exec(word);
  if (scotsPrefixed) {
    const stem = scotsPrefixed[1];
    return `Mc${stem.charAt(0).toUpperCase()}${stem.slice(1)}`;
  }
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function formatCityName(
  city?: string | null,
  state?: string | null,
): string {
  if (!city) return "";
  const trimmed = city.trim();
  if (trimmed !== trimmed.toLowerCase()) return trimmed;

  const canonical = state
    ? CITY_CASING_EXCEPTIONS.get(`${state.trim().toUpperCase()}:${trimmed}`)
    : undefined;
  if (canonical) return canonical;

  const segments = trimmed.split(CITY_WORD_BOUNDARY);
  let wordIndex = 0;
  return segments
    .map((segment, index) => {
      if (CITY_WORD_BOUNDARY.test(segment) || segment === "") return segment;
      const isMinor = wordIndex > 0 && CITY_MINOR_WORDS.has(segment);
      const isPossessive = segments[index - 1] === "'" && segment.length === 1;
      wordIndex += 1;
      return isMinor || isPossessive ? segment : capitaliseCityWord(segment);
    })
    .join("");
}

export function formatLocation(
  postalCode: string,
  city?: string | null,
  state?: string | null,
): string {
  const cityName = formatCityName(city, state);
  if (cityName && state) return `${cityName}, ${state} ${postalCode}`;
  if (state) return `${state} ${postalCode}`;
  return postalCode;
}

export function formatListingLocation(
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
): string {
  const cityName = formatCityName(city, state);
  if (!cityName && !state) return "";
  if (cityName && state)
    return `${cityName}, ${state}${postalCode ? ` ${postalCode}` : ""}`;
  return [cityName, state, postalCode].filter(Boolean).join(" ");
}

export function formatProductLocation(location: {
  cityName?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  postalCode?: string | null;
}): string {
  const state = location.cityName
    ? (location.stateCode ?? location.stateName)
    : (location.stateName ?? location.stateCode);
  return (
    formatListingLocation(location.cityName, state, location.postalCode) || "—"
  );
}
